// src/redux/slices/CustomFieldsSlice.js
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosClient from "../../axios-client";

// ──────────────────────────────────────────────────────────────
//  ADD CUSTOM FIELD
// ──────────────────────────────────────────────────────────────
export const AddCustomFields = createAsyncThunk(
  "customFields/addCustomFields",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosClient.post(
        "category/addCategoryCustomLink",
        data
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// ──────────────────────────────────────────────────────────────
//  GET CUSTOM FIELDS
// ──────────────────────────────────────────────────────────────
export const GetCustomFields = createAsyncThunk(
  "customFields/getCustomFields",
  async (categoryId, { rejectWithValue }) => {
    try {
      const response = await axiosClient.get(
        "category/listCategoryCustomLink",
        {
          params: { categoryId, pageSize: 100, pageNumber: 1 },
        }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

// ──────────────────────────────────────────────────────────────
//  DELETE CUSTOM FIELD
// ──────────────────────────────────────────────────────────────
export const deleteField = createAsyncThunk(
  "customFields/deleteCustomFields",
  async ({ id, params }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.delete(
        `category/deleteCategoryCustomLink/${id}`,
        { params }
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue({ id, error: error.response?.data });
    }
  }
);

// ──────────────────────────────────────────────────────────────
//  UPDATE CUSTOM FIELD
// ──────────────────────────────────────────────────────────────
export const updateField = createAsyncThunk(
  "customFields/updateCustomFields",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await axiosClient.put(
        `category/updateCategoryCustomLink/${id}`,
        data
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue({
        id,
        error: error.response?.data,
        oldData: data,
      });
    }
  }
);

// ──────────────────────────────────────────────────────────────
//  INITIAL STATE
// ──────────────────────────────────────────────────────────────
const initialState = {
  addingField: false,
  updatingField: false,
  errorAddingField: null,
  errorUpdatingField: null,
  addedField: null,

  catCustomFields: [],
  loadingCustomFields: false,
  errorCustomFields: null,
  totalQuestions: 0,
};

// ──────────────────────────────────────────────────────────────
//  SLICE
// ──────────────────────────────────────────────────────────────
const CustomFieldsSlice = createSlice({
  name: "customFields",
  initialState,
  reducers: {
    setCatcustomFields: (state, action) => {
      state.catCustomFields = action.payload;
    },
  },
  extraReducers: (builder) => {
    // ───── ADD ─────
    builder
      .addCase(AddCustomFields.pending, (state) => {
        state.addingField = true;
        state.errorAddingField = null;
      })
      .addCase(AddCustomFields.fulfilled, (state, action) => {
        state.addingField = false;
        state.errorAddingField = null;
        state.addedField = true;

        const newRecord = action.payload?.data ?? action.meta.arg;
        if (newRecord) {
          // Replace temp record if exists
          const tempId = action.meta.arg?.tempId;
          if (tempId) {
            const idx = state.catCustomFields.findIndex((f) => f.id === tempId);
            if (idx !== -1) state.catCustomFields[idx] = newRecord;
            else state.catCustomFields.push(newRecord);
          } else {
            state.catCustomFields.push(newRecord);
          }
        }
      })
      .addCase(AddCustomFields.rejected, (state, action) => {
        state.addingField = false;
        state.errorAddingField = action.payload;
        const tempId = action.meta.arg?.tempId;
        if (tempId) {
          state.catCustomFields = state.catCustomFields.filter(
            (f) => f.id !== tempId
          );
        }
      })

      // ───── GET ─────
      .addCase(GetCustomFields.pending, (state) => {
        state.loadingCustomFields = true;
        state.errorCustomFields = null;
        state.catCustomFields = [];
        state.totalQuestions = 0;
      })
      .addCase(GetCustomFields.fulfilled, (state, action) => {
        state.loadingCustomFields = false;
        state.catCustomFields = action.payload?.data ?? [];
        state.totalQuestions = action.payload?.totalItems ?? 0;
        state.errorCustomFields = null;
      })
      .addCase(GetCustomFields.rejected, (state, action) => {
        state.loadingCustomFields = false;
        state.errorCustomFields = action.payload;
        state.catCustomFields = [];
        state.totalQuestions = 0;
      })

      // ───── DELETE ─────
      .addCase(deleteField.pending, (state, action) => {
        const { id } = action.meta.arg;
        state.catCustomFields = state.catCustomFields.filter(
          (f) => f.id !== id
        );
      })
      .addCase(deleteField.fulfilled, () => {})
      .addCase(deleteField.rejected, (state, action) => {
        // Optionally refetch or show toast
      })

      // ───── UPDATE ─────
      .addCase(updateField.pending, (state, action) => {
        state.updatingField = true;
        state.errorUpdatingField = null;
        const { id, data } = action.meta.arg;
        const idx = state.catCustomFields.findIndex((f) => f.id === id);
        if (idx !== -1) {
          state.catCustomFields[idx] = {
            ...state.catCustomFields[idx],
            ...data,
          };
        }
      })
      .addCase(updateField.fulfilled, (state, action) => {
        state.updatingField = false;
        const { id, data } = action.payload;
        const idx = state.catCustomFields.findIndex((f) => f.id === id);
        if (idx !== -1) {
          state.catCustomFields[idx] = data.data || data;
        }
      })
      .addCase(updateField.rejected, (state, action) => {
        state.updatingField = false;
        state.errorUpdatingField = action.payload?.error;
        const { id, oldData } = action.payload || {};
        if (id && oldData) {
          const idx = state.catCustomFields.findIndex((f) => f.id === id);
          if (idx !== -1) {
            state.catCustomFields[idx] = oldData;
          }
        }
      });
  },
});

export const { setCatcustomFields } = CustomFieldsSlice.actions;
export default CustomFieldsSlice.reducer;
